import React from 'react'
import Layout from '../components/layout2'
import Container from '../components/container'
import Vbox from '../components/vbox'
import Top from '../components/top'
import Summary from '../components/summary'
import Button from '../components/button'
import Img from '../components/img'

class RootIndex extends React.Component {
  render() {
    return (
      <Layout>
        <Container>
          <Vbox widegap>
            <Top
              title="Library Forum"
              body="The new library and the central meeting place for the residents of Jordbro."
              image="/forum.png"
            />
            <Summary>
              <h4 className="h4">Role</h4>
              <p className="small">Designer / Architect</p>
              <h4 className="h4">Assignment</h4>
              <p className="small">
                My assignment as an architecture student was to develop a
                proposal for the new library in Jordbro based on a given site
                and a program (which and how large functional areas should be
                included - requirements from the client).
              </p>
              <h4 className="h4">Timeframe</h4>
              <p className="small">11 weeks (2020)</p>
              <h4 className="h4">Why an architecture project?</h4>
              <p className="small">
                UX is not just something that relates to the digital world but
                also our physical world. In this project I worked with many
                similar user-centered methods and design processes as for the
                digital UX projects, to eventually land in a proposal.
              </p>
              <h4 className="h4">Results</h4>
              <p className="small">
                I conceptualized several ideas with the purpose of creating a
                natural meeting place for Jordbro's residents.
              </p>
              <p className="small">
                This resulted in a central library/forum for knowledge exchange
                where the strong association culture in the area gets to take
                place and be in focus rather than books. A building whose
                overall structure is shaped after the clearest movement patterns
                over the square to invite and encourage spontaneous visits.
              </p>
            </Summary>
            <div>
              <h2 className="h2">1 - Background & Overview: </h2>
              <Container narrow>
                <h3 className="h3">Jordbro</h3>
                <p className="body">
                  A new center is planned adjacent to the commuter train station
                  in Jordbro, located in Haninge municipality. The project
                  includes a new bus terminal, a square, approximately 270 new
                  homes, a preschool and a library.
                </p>
                <p className="body">
                  The goal is to preserve Jordbro's small-town character, with
                  focus on walking distance to the commuter train station and
                  buildings that harmonize with the existing Jordbro center. The
                  area is characterized today by a mix of multi-family houses
                  and small houses, mainly from the 60s and 70s.
                </p>
                <p className="body">
                  Jordbro is home to a diversity of people from different
                  cultures and parts of the world, which enriches society. One
                  of the unique aspects of Jordbro is its lively association
                  life, which plays a central role for engagement in the
                  locality.
                </p>
              </Container>
            </div>

            <div>
              <h2 className="h2">2 - Results:</h2>
              <Container narrow>
                <h3 className="h3">Floor plans and visualizations</h3>
                <h4 className="h4">Library forum</h4>
                <p className="body">
                  Library Forum is an engaging meeting place for new ideas in
                  Jordbro.
                </p>
                <p className="body">
                  In an increasingly digital world where the physical book gets
                  less space, the definition of what a library is changes. With
                  internet as primary knowledge source, "filter bubbles" also
                  arise, one way to burst them is to meet other people, get
                  exchange and be inspired. But to be able to be inspired, you
                  must be exposed.
                </p>
                <Img src="/forum1.png" caption="Visualizations" />
                <Img src="/forum2.png" caption="Floor plans" />
                <h4 className="h4">Design</h4>
                <p className="body">
                  Library forum is designed after two main paths over the square
                  that are translated into two orthogonal passages through the
                  library's entrance floor where premises connected to
                  association activities get to take place. Further up in the
                  house, these passages become voids and bridges that give sight
                  all the way up to the sky.
                </p>
                <p className="body">
                  This form makes accessible and exposes the engagement and
                  activities that exist in Jordbro.
                </p>
              </Container>
            </div>
            <div>
              <h2 className="h2">3 - Process:</h2>
              <Container narrow>
                <h3 className="h3">Processmodell</h3>
                <Img narrow src="/forum3.png" />
              </Container>
            </div>
            <div>
              <Container narrow>
                <h3 className="h3">Research</h3>
                <Img
                  narrow
                  src="/forum4.png"
                  caption="Important paths and flows in Jordbro center"
                />
                <h4 className="h4">Site analysis</h4>
                <p className="body">
                  I made recurring visits to the site to gain an understanding
                  of how the area is built up and structured. Questions that I
                  worked around for data collection: How does the flow look?
                  What are the main paths? What typology exists? What green
                  areas are there? What are the most important nodes? What is
                  important for the residents of Jordbro?
                </p>
                <h4 className="h4">Insights and scope</h4>
                <p className="body">
                  The answers to the questions I asked in the site analysis
                  helped me to get direction in the design process by making
                  decisions about what I wanted to work further with. In this
                  case I anchored the design in the paths and flows that got a
                  decisive role for the overall design of the building, but also
                  insights from interviews with residents that highlighted
                  Jordbro as a place where association life and meeting between
                  people played an important role.
                </p>
                <h4 className="h4">Program analysis</h4>
                <p className="body">
                  Since it was a large program with many functions that should
                  fit on a limited area, it needed to be sorted into the
                  different parts to be able to make reasonable design
                  decisions. I worked with many different diagrams in both plan
                  and volume to among other things understand the spatial
                  relationships, the relationship between user and function,
                  private and public areas, which functions are used by most
                  users etc. This helped me to make decisions about which
                  functions should be placed where in the building.
                </p>
                <Img
                  narrow
                  src="/forum5.png"
                  caption="Program study in volume"
                />
                <Img narrow src="/forum6.png" caption="Program study in plan" />
                <h4 className="h4">Insights and overall design decisions</h4>
                <p className="body">
                  Since association life and meeting between people played a big
                  role in Jordbro, I chose to connect the activities and
                  functions that should promote these activities. Such as
                  lecture hall, workshop room, exhibition space and café and
                  place these in the most easily accessible and most exposed
                  place in the building.
                </p>
              </Container>
            </div>
            <div>
              <Container narrow>
                <h3 className="h3">Concept & Design</h3>
                <Img narrow src="/forum7.png" caption="Concept sketches" />
                <Img
                  narrow
                  src="/forum8.png"
                  caption="Concept sketches in 3D"
                />
                <h4 className="h4">Insights</h4>
                <p className="body">
                  The sketches illustrate tests of the concept around flows
                  through the building, where proposal C became the variant I
                  chose to develop further.
                </p>
              </Container>
            </div>
            <div>
              <h2 className="h2">4 - Summary:</h2>
              <Container narrow>
                <h3 className="h3">Insights & Learnings</h3>
                <h4 className="h4">The design process</h4>
                <p className="body">
                  Good design is anchored and built on insights. Setting up
                  frameworks that help creativity gives direction
                </p>
                <h4 className="h4">Dare to be critical</h4>
                <p className="body">
                  Redefining what a library can be helped me see new
                  possibilities. What was the need? In this case the library was
                  not about a place to borrow books but more about meeting
                  between people and various activities. It was important to be
                  able to redefine the concept to be able to solve the problem
                  in the right way for the intended target groups.{' '}
                </p>
                <h4 className="h4">Iteration</h4>
                <p className="body">
                  There is no shortcut to a good end result.
                </p>
              </Container>
            </div>

            <div>
              <h2 className="h2">More projects:</h2>
              <div className="more-wrapper">
                <div class="more">
                  <img src="/vattenfall.png" />
                  <h3 className="h3">Design system B2B portals</h3>
                  <Button
                    to="/vattenfall"
                    ariaLabel="Explore the full project for Design system B2B portals"
                  >
                    Explore full project
                  </Button>
                </div>
                <div class="more">
                  <img src="/jhl.png" />
                  <h3 className="h3">Jagharlast.se</h3>
                  <Button
                    to="/jagharlast"
                    ariaLabel="Explore the full project for Jagharlast.se"
                  >
                    Explore full project
                  </Button>
                </div>
              </div>
            </div>
          </Vbox>
        </Container>
      </Layout>
    )
  }
}

export default RootIndex
